/* ============================================
   Navegación inferior del portal (móvil / PWA)
   Reemplaza al sidebar en pantallas chicas: pocas opciones a mano y el
   pulgar cerca. "Apps" no navega: cambia la barra al nivel de las apps del
   cliente, con vuelta atrás. Lo demás vive en la hoja "Más".
   ============================================ */

import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useApi } from '@/hooks/useApi'
import { useAuth } from '@/hooks/useAuth'
import { usePortalCliente } from '@/hooks/usePortalCliente'
import type { MiApp, PortalAppsResponse } from '@/lib/types'
import { puedeVerFacturacion } from '@/lib/portal-permisos'

interface Props {
  theme: 'light' | 'dark'
  onToggleTheme: () => void
}

function Icono({ d, fill }: { d: string; fill?: boolean }) {
  return (
    <svg width="21" height="21" viewBox="0 0 24 24" fill={fill ? 'currentColor' : 'none'}
         stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d={d} />
    </svg>
  )
}

const IC = {
  resumen: 'M3 13h8V3H3v10zm10 8h8V11h-8v10zM3 21h8v-6H3v6zM13 3v6h8V3h-8z',
  facturas: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6 M16 13H8 M16 17H8',
  apps: 'M4 4h6v6H4z M14 4h6v6h-6z M14 14h6v6h-6z M4 14h6v6H4z',
  mas: 'M4 6h16 M4 12h16 M4 18h16',
  volver: 'M19 12H5 M12 19l-7-7 7-7',
}

export default function PortalBottomNav({ theme, onToggleTheme }: Props) {
  const navigate = useNavigate()
  const location = useLocation()
  const api = useApi()
  const { afiliaciones, activeClienteId } = usePortalCliente()
  const { user, contactoId, signOut } = useAuth()

  // Comprar y ver la facturación son de PRINCIPAL/FACTURACIÓN (o titular legado).
  const rolesAfil = afiliaciones.find(a => a.cliente_id === activeClienteId)?.roles as string[] | undefined
  const roles: string[] = rolesAfil ?? user?.roles ?? []
  const puedeComprar = puedeVerFacturacion(roles, contactoId)
  const verFacturacion = puedeComprar

  async function onLogout() {
    await signOut()
    navigate('/login', { replace: true })
  }

  const [nivel, setNivel] = useState<'principal' | 'apps'>('principal')
  const [hojaAbierta, setHojaAbierta] = useState(false)
  const [misApps, setMisApps] = useState<MiApp[]>([])
  const [tienePlanHoras, setTienePlanHoras] = useState(false)
  const [tieneHosting, setTieneHosting] = useState(false)

  useEffect(() => {
    if (!activeClienteId) return
    api.get<PortalAppsResponse>(`/api/portal/apps?cliente_id=${activeClienteId}`)
      .then(d => setMisApps((d?.mis_apps ?? []).filter(a => a.estado !== 'revocada')))
      .catch(() => setMisApps([]))
    api.get<{ tiene_plan?: boolean }>(`/api/portal/plan-horas?cliente_id=${activeClienteId}`)
      .then(d => setTienePlanHoras(!!d?.tiene_plan)).catch(() => setTienePlanHoras(false))
    api.get<unknown[]>(`/api/hosting/mi-hosting?cliente_id=${activeClienteId}`)
      .then(d => setTieneHosting(Array.isArray(d) && d.length > 0)).catch(() => setTieneHosting(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeClienteId])

  // Al salir de /portal/apps la barra vuelve a su nivel principal.
  useEffect(() => {
    if (!location.pathname.startsWith('/portal/apps')) setNivel('principal')
  }, [location.pathname])

  const ir = (to: string) => { setHojaAbierta(false); navigate(to) }
  const activo = (p: string, exacto = false) =>
    exacto ? location.pathname === p : location.pathname.startsWith(p)

  return (
    <>
      {hojaAbierta && <div className="pbn-backdrop" onClick={() => setHojaAbierta(false)} />}

      {hojaAbierta && (
        <div className="pbn-hoja" role="dialog" aria-label="Más opciones">
          <div className="pbn-hoja-tirador" />

          {afiliaciones.length > 1 && (
            <div className="pbn-hoja-grupo">
              <span className="pbn-hoja-titulo">Empresa</span>
              {afiliaciones.map(a => (
                <button
                  key={a.cliente_id}
                  className={`pbn-hoja-item ${a.cliente_id === activeClienteId ? 'pbn-hoja-item--activo' : ''}`}
                  onClick={() => { setHojaAbierta(false); navigate('/seleccionar-cuenta') }}
                >
                  {a.empresa_nombre}
                </button>
              ))}
            </div>
          )}

          <div className="pbn-hoja-grupo">
            <span className="pbn-hoja-titulo">Secciones</span>
            <button className="pbn-hoja-item" onClick={() => ir('/portal/proyectos')}>Proyectos</button>
            {tienePlanHoras && <button className="pbn-hoja-item" onClick={() => ir('/portal/horas')}>Horas / Plan</button>}
            {tieneHosting && <button className="pbn-hoja-item" onClick={() => ir('/portal/hosting')}>Hosting</button>}
            {verFacturacion && <button className="pbn-hoja-item" onClick={() => ir('/portal/suscripcion')}>Suscripción</button>}
            <button className="pbn-hoja-item" onClick={() => ir('/portal/perfil')}>Mi perfil</button>
          </div>

          <div className="pbn-hoja-grupo">
            <span className="pbn-hoja-titulo">Acciones</span>
            {puedeComprar && <button className="pbn-hoja-item" onClick={() => ir('/portal/nuevo-pedido')}>Servicios</button>}
            <a className="pbn-hoja-item" href="https://wa.me/51986252983?text=Hola%2C%20necesito%20ayuda" target="_blank" rel="noreferrer">Soporte</a>
            <button className="pbn-hoja-item" onClick={onToggleTheme}>
              {theme === 'dark' ? 'Modo claro' : 'Modo oscuro'}
            </button>
            <button className="pbn-hoja-item pbn-hoja-item--danger" onClick={onLogout}>Cerrar sesión</button>
          </div>
        </div>
      )}

      <nav className="pbn" aria-label="Navegación principal">
        {nivel === 'principal' ? (
          <>
            <button className={`pbn-item ${activo('/portal', true) ? 'pbn-item--activo' : ''}`} onClick={() => ir('/portal')}>
              <Icono d={IC.resumen} /><span>Resumen</span>
            </button>
            {verFacturacion && (
              <button className={`pbn-item ${activo('/portal/facturas') ? 'pbn-item--activo' : ''}`} onClick={() => ir('/portal/facturas')}>
                <Icono d={IC.facturas} /><span>Facturas</span>
              </button>
            )}
            {misApps.length > 0 && (
              <button className={`pbn-item ${activo('/portal/apps') ? 'pbn-item--activo' : ''}`} onClick={() => setNivel('apps')}>
                <Icono d={IC.apps} /><span>Apps</span>
              </button>
            )}
            <button className={`pbn-item ${hojaAbierta ? 'pbn-item--activo' : ''}`} onClick={() => setHojaAbierta(v => !v)}>
              <Icono d={IC.mas} /><span>Más</span>
            </button>
          </>
        ) : (
          <>
            <button className="pbn-item pbn-item--volver" onClick={() => setNivel('principal')}>
              <Icono d={IC.volver} /><span>Volver</span>
            </button>
            {misApps.slice(0, 3).map(a => (
              <button
                key={a.id}
                className={`pbn-item ${location.pathname === `/portal/apps/${a.app_slug}` ? 'pbn-item--activo' : ''}`}
                onClick={() => ir(`/portal/apps/${a.app_slug}`)}
              >
                {a.icono_url
                  ? <img className="pbn-logo" src={a.icono_url} alt="" />
                  : <span className="pbn-logo pbn-logo--txt">{(a.nombre ?? a.app_slug).charAt(0).toUpperCase()}</span>}
                <span>{(a.nombre ?? a.app_slug).split(' ')[0]}</span>
              </button>
            ))}
            <button className={`pbn-item ${activo('/portal/apps') && location.pathname === '/portal/apps' ? 'pbn-item--activo' : ''}`} onClick={() => ir('/portal/apps')}>
              <Icono d={IC.apps} /><span>Todas</span>
            </button>
          </>
        )}
      </nav>
    </>
  )
}
